import { FormControlStatus } from '@angular/forms';
import { FuelSpecMinMaxValues } from '@navstation-portal/fuel-specs/data-model/fuel-spec';
import { FuelSpecsTableVm, OffshoreFuelSpecFormState } from '@navstation-portal/reporting/data-model/offshore-report';
import {
  FormSectionState,
  IReport,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { NavStatus } from '@navstation-portal/shared/data-model/ui';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { getFieldFromState, isStateField } from '../../offshore-vm-builder-helpers';
import { getIsoGradeOptions } from '../utils/fuel-util';
import { buildFuelTypeOptionsByRow, getIsoGradeOptionsByRow } from './offshore-fuel-spec-options';

const FUEL_SPEC_LIMIT_KEYS: Partial<
  Record<string, { min: keyof FuelSpecMinMaxValues; max: keyof FuelSpecMinMaxValues }>
> = {
  density: { min: 'density_15_min', max: 'density_15_max' },
  lcv: { min: 'lcv_min', max: 'lcv_max' },
  sulphur: { min: 'sulphur_min', max: 'sulphur_max' },
  viscosity: { min: 'viscosity_40_min', max: 'viscosity_40_max' },
};

export type OffshoreConsumptionsVm = ReturnType<typeof buildOffshoreConsumptionsVm>;

function getFieldLimits(field: string, limits?: FuelSpecMinMaxValues): { min?: number; max?: number } {
  if (!limits) return { min: undefined, max: undefined };

  const keys = FUEL_SPEC_LIMIT_KEYS[field];
  if (!keys) return { min: undefined, max: undefined };

  return {
    min: limits[keys.min],
    max: limits[keys.max],
  };
}

function isValueOutOfRange(value: unknown, min?: number, max?: number): boolean {
  if (typeof value !== 'number' || Number.isNaN(value)) return false;
  if (typeof min === 'number' && value < min) return true;
  if (typeof max === 'number' && value > max) return true;
  return false;
}

function isEmptyFieldValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  return typeof value === 'number' && Number.isNaN(value);
}

function getInvalidTooltip(field: string, fuelType?: string, limits?: FuelSpecMinMaxValues): string | undefined {
  const resolvedFuelType = fuelType || 'Fuel';
  const isIsoFuelGradeRequired = !!fuelType && getIsoGradeOptions({ code: fuelType, name: fuelType }).length > 1;

  if (field === 'isoFuelGrade') {
    return isIsoFuelGradeRequired ? 'ISO Fuel Grade is required' : undefined;
  }
  if (field === 'fuelTemp') return `${resolvedFuelType} fuel temperature is required`;

  if (!limits) return `${resolvedFuelType} limits are unavailable`;

  if (field === 'lcv') {
    const hasMin = typeof limits.lcv_min === 'number';
    const hasMax = typeof limits.lcv_max === 'number';
    if (hasMin && hasMax) return `Low Calorific Value (LCV) must be between ${limits.lcv_min} and ${limits.lcv_max}`;
    if (hasMin) return `Low Calorific Value (LCV) must be at least ${limits.lcv_min}`;
    if (hasMax) return `Low Calorific Value (LCV) must be no more than ${limits.lcv_max}`;
    return `${resolvedFuelType} LCV is required`;
  }

  if (field === 'density') {
    const hasMin = typeof limits.density_15_min === 'number';
    const hasMax = typeof limits.density_15_max === 'number';
    if (hasMin && hasMax) return `Density must be between ${limits.density_15_min} and ${limits.density_15_max}`;
    if (hasMin) return `Density must be at least ${limits.density_15_min}`;
    if (hasMax) return `Density must be no more than ${limits.density_15_max}`;
    return 'Density is required';
  }

  if (field === 'sulphur') {
    const hasMin = typeof limits.sulphur_min === 'number';
    const hasMax = typeof limits.sulphur_max === 'number';
    if (hasMin && hasMax) return `Sulphur percentage must be between ${limits.sulphur_min} and ${limits.sulphur_max}`;
    if (hasMin) return `Sulphur percentage must be at least ${limits.sulphur_min}`;
    if (hasMax) return `Sulphur percentage must be no more than ${limits.sulphur_max}`;
    return 'Sulphur percentage is required';
  }

  if (field === 'viscosity') {
    const hasMin = typeof limits.viscosity_40_min === 'number';
    const hasMax = typeof limits.viscosity_40_max === 'number';
    if (hasMin && hasMax)
      return `Viscosity at 40°C must be between ${limits.viscosity_40_min} and ${limits.viscosity_40_max}`;
    if (hasMin) return `Viscosity at 40°C must be at least ${limits.viscosity_40_min}`;
    if (hasMax) return `Viscosity at 40°C must be no more than ${limits.viscosity_40_max}`;
    return undefined;
  }

  return undefined;
}

function buildRangeValidatedField<T extends { value?: unknown; status?: string }>(
  field: string,
  fieldState: T,
  limits: FuelSpecMinMaxValues | undefined,
  fuelGrade?: string
) {
  const rangeLimits = getFieldLimits(field, limits);
  const outOfRange = isValueOutOfRange(fieldState?.value, rangeLimits.min, rangeLimits.max);
  const isEmpty = isEmptyFieldValue(fieldState?.value);
  const isOptionalField = field === 'viscosity';

  // Keep required-empty INVALID for required fields.
  // Clear stale range INVALID when a non-empty value is now within range.
  // Also clear empty INVALID for optional range fields (viscosity).
  return {
    ...fieldState,
    ...rangeLimits,
    status: outOfRange
      ? 'INVALID'
      : fieldState?.status === 'INVALID' && ((!isEmpty && !outOfRange) || (isOptionalField && isEmpty))
        ? 'VALID'
        : fieldState?.status,
    error: getInvalidTooltip(field, fuelGrade, limits),
  };
}

export function buildFuelSpecsTableVm(
  entries: OffshoreFuelSpecFormState[],
  getFuelSpecMinMaxValuesByGrade?: (fuelGrade?: string, isoFuelGrade?: string) => FuelSpecMinMaxValues | undefined
): FuelSpecsTableVm {
  if (!getFuelSpecMinMaxValuesByGrade || !entries.length) {
    return {
      entries: [],
      autoApplyIsoGradeByRow: [],
    };
  }

  const vmEntries = entries.map((entry) => {
    const fuelGrade = entry.fuelType?.value?.code as string | undefined;
    const isoFuelGrade = entry.isoFuelGrade?.value?.code as string | undefined;
    const limits = getFuelSpecMinMaxValuesByGrade?.(fuelGrade, isoFuelGrade);

    return {
      ...entry,
      isoFuelGrade: {
        ...entry.isoFuelGrade,
        error: getInvalidTooltip('isoFuelGrade', fuelGrade, limits),
      },
      lcv: buildRangeValidatedField('lcv', entry.lcv, limits, fuelGrade),
      sulphur: buildRangeValidatedField('sulphur', entry.sulphur, limits, fuelGrade),
      density: buildRangeValidatedField('density', entry.density, limits, fuelGrade),
      fuelTemp: {
        ...entry.fuelTemp,
        error: getInvalidTooltip('fuelTemp', fuelGrade, limits),
      },
      viscosity: buildRangeValidatedField('viscosity', entry.viscosity, limits, fuelGrade),
    };
  });

  const autoApplyIsoGradeByRow = vmEntries.map((entry, index) => {
    if (!entry.fuelType?.value || entry.isoFuelGrade?.value) return undefined;

    const selectableIsoOptions = getIsoGradeOptionsByRow(vmEntries, index).filter(
      (option) => !option.disabled
    ) as SelectOption[];

    return selectableIsoOptions.length === 1 ? selectableIsoOptions[0] : undefined;
  });

  return {
    entries: vmEntries,
    autoApplyIsoGradeByRow,
  };
}

export interface OffshoreConsumptionFormVmSections {
  [key: string]: (() => FormSectionState | undefined) | undefined;
  fuelSpecs: () => FormSectionState;
  totalConsumptions: () => FormSectionState;
  auxEngineConsumptions: () => FormSectionState;
  boilerConsumptions: () => FormSectionState;
  cylinderOilConsumption: () => FormSectionState;
}

export function buildOffshoreConsumptionsVm(
  state: OffshoreConsumptionFormVmSections,
  powerCarryOver: IReport['power'],
  vesselSpec: VesselSpec | undefined,
  availableFuelTypeOptions: SelectOption[],
  getFuelSpecMinMaxValuesByGrade?: (fuelGrade?: string, isoFuelGrade?: string) => FuelSpecMinMaxValues | undefined
) {
  const calculateCylinderOilKgConsumption = () => {
    const actualConsumptionLitres = getCylinderOilValue('actualConsumptionLitres', state);
    const densityAt15 = getCylinderOilValue('oilDensityAt15', state);
    const tankTemperature = getCylinderOilValue('oilTankTemperature', state);

    const result = ((actualConsumptionLitres * densityAt15) / 1000) * (1 - (tankTemperature - 15) * 0.00064);

    return Math.round(result * 100) / 100;
  };

  const calculateCylinderOilSloc = (oilKgConsumption: number) => {
    const mainEnginePower = powerCarryOver?.mainEngine.power;
    if (mainEnginePower == null || isNaN(mainEnginePower)) return 0;

    const mainEngineRhDiff = powerCarryOver.machineryRh[0]?.difference;
    if (mainEngineRhDiff == null || isNaN(mainEngineRhDiff)) return 0;

    // TODO TCRPM
    const tcRpmPower = undefined;
    const useTcRpmPower = vesselSpec?.information.use_tcrpm_power;
    const power = useTcRpmPower ? tcRpmPower : mainEnginePower;
    if (power == null) return 0;

    return (oilKgConsumption * 1000) / (power * mainEngineRhDiff);
  };

  const calculateAEBasicLoad = (index: number) => {
    const tcSection = state.totalConsumptions();
    if (!tcSection) return 0;

    const tcEntries = tcSection['entries'];
    if (!tcEntries || isStateField(tcEntries)) return 0;

    const tcEntry = tcEntries[index];
    if (!tcEntry) return 0;

    const aeSection = state.auxEngineConsumptions();

    if (!aeSection) return 0;

    const aeEntries = aeSection['entries'];
    if (!aeEntries || isStateField(aeEntries)) return 0;

    const aeEntry = aeEntries[index];
    if (!aeEntry) return 0;

    const totalAeConsumption = (tcEntry['auxEngines'].value as number) || 0;
    if (totalAeConsumption == null || isNaN(totalAeConsumption)) return 0;

    let aeBreakdown = 0;
    Object.keys(aeEntry).forEach((key) => {
      if (key === 'basicLoad') return;
      const value = aeEntry[key].value as number;
      if (value != null && !isNaN(value)) aeBreakdown += value;
    });

    return totalAeConsumption - aeBreakdown;
  };

  const calculateABBasicLoad = (index: number) => {
    const tcSection = state.totalConsumptions();
    if (!tcSection) return 0;

    const tcEntries = tcSection['entries'];
    if (!tcEntries || isStateField(tcEntries)) return 0;

    const tcEntry = tcEntries[index];
    if (!tcEntry) return 0;

    const abSection = state.boilerConsumptions();
    if (!abSection) return 0;

    const abEntries = abSection['entries'];
    if (!abEntries || isStateField(abEntries)) return 0;

    const abEntry = abEntries[index];
    if (!abEntry) return 0;

    const totalAbConsumption = (tcEntry['boiler'].value as number) || 0;
    if (totalAbConsumption == null || isNaN(totalAbConsumption)) return 0;

    let abBreakdown = 0;
    Object.keys(abEntry).forEach((key) => {
      if (key === 'basicLoad') return;
      const value = abEntry[key].value as number;
      if (value != null && !isNaN(value)) abBreakdown += value;
    });

    return totalAbConsumption - abBreakdown;
  };

  const calculateTotal = (index: number) => calculateConsumptionsTotal(index, state);

  const isRowValidFuelSpec = (field: string) => areFieldsValid(state.fuelSpecs(), field);

  const lcvFieldsStatus = () => (isRowValidFuelSpec('lcv') ? 'valid' : 'invalid');

  const sulphurFieldsStatus = () => (isRowValidFuelSpec('sulphur') ? 'valid' : 'invalid');

  const densityFieldsStatus = () => (isRowValidFuelSpec('density') ? 'valid' : 'invalid');

  const fuelSpecStatusFields: Array<keyof OffshoreFuelSpecFormState> = [
    'fuelType',
    'isoFuelGrade',
    'lcv',
    'sulphur',
    'density',
    'fuelTemp',
    'viscosity',
  ];

  const resolveFieldStatus = (
    entry: OffshoreFuelSpecFormState,
    field: keyof OffshoreFuelSpecFormState
  ): FormControlStatus => {
    const fieldState = entry[field];
    const fuelGrade = entry.fuelType?.value?.code as string | undefined;
    const isoFuelGrade = entry.isoFuelGrade?.value?.code as string | undefined;
    const limits = getFuelSpecMinMaxValuesByGrade?.(fuelGrade, isoFuelGrade);
    const { min, max } = getFieldLimits(field as string, limits);
    const hasRangeLimits = min !== undefined || max !== undefined;
    // Range fields are density, lcv, sulphur, viscosity — those in FUEL_SPEC_LIMIT_KEYS.
    const isRangeField = (field as string) in FUEL_SPEC_LIMIT_KEYS;
    const isEmpty = isEmptyFieldValue(fieldState.value);
    const isOptionalField = field === 'viscosity';

    if (hasRangeLimits && isValueOutOfRange(fieldState.value, min, max)) {
      return 'INVALID';
    }

    if (
      isRangeField &&
      fieldState.status === 'INVALID' &&
      ((!isEmpty && !isValueOutOfRange(fieldState.value, min, max)) || (isOptionalField && isEmpty))
    ) {
      return 'VALID';
    }

    return fieldState.status;
  };

  const getEntryStatus = (entry: OffshoreFuelSpecFormState): NavStatus => {
    const statuses = fuelSpecStatusFields.map((field) => resolveFieldStatus(entry, field));

    if (statuses.some((status) => status === 'INVALID')) return 'invalid';
    if (statuses.some((status) => status !== 'VALID' && status !== 'DISABLED')) {
      return 'pending';
    }
    return 'valid';
  };

  const fuelSpecEntriesValid = (): NavStatus => {
    const fuelSpecsSection: FormSectionState = state.fuelSpecs();
    if (!fuelSpecsSection) return 'pending';

    const entries = fuelSpecsSection['entries'] as unknown as OffshoreFuelSpecFormState[] | undefined;
    if (!entries) return 'valid';

    const activeEntries = entries.filter((entry) => !!entry.fuelType?.value);
    if (activeEntries.length === 0) return 'valid';

    const hasEntryMissingIsoGrade = activeEntries.some((entry) => !entry.isoFuelGrade.value);
    if (hasEntryMissingIsoGrade) return 'invalid';

    const statuses = activeEntries.map(getEntryStatus);
    if (statuses.includes('invalid')) return 'invalid';
    if (statuses.includes('pending')) return 'pending';
    return 'valid';
  };

  const totalConsumptionsValid = (): NavStatus => {
    const totalConsumptionsSection: FormSectionState = state.totalConsumptions();
    if (!totalConsumptionsSection) return 'pending';
    const entries = totalConsumptionsSection['entries'];
    if (!entries || isStateField(entries)) return 'pending';

    const entriesState = entries as unknown as Record<string, Record<string, StateField<StateFieldType>>>;
    const entryStatuses = Object.values(entriesState).map((entry) => {
      const fieldStatuses = Object.values(entry).map((field) => field.status);
      if (fieldStatuses.some((status) => status === 'INVALID')) return 'invalid';
      if (fieldStatuses.some((status) => status !== 'VALID' && status !== 'DISABLED')) return 'pending';
      return 'valid';
    });

    if (entryStatuses.some((status) => status === 'invalid')) return 'invalid';
    if (entryStatuses.some((status) => status === 'pending')) return 'pending';
    return 'valid';
  };

  const fuelTempFieldsStatus = () => (isRowValidFuelSpec('fuelTemp') ? 'valid' : 'invalid');

  const fuelSpecsStatus = (): NavStatus => fuelSpecEntriesValid();

  const totalConsumptionsStatus = (): NavStatus => totalConsumptionsValid();

  const cylinderOilKgConsumption = calculateCylinderOilKgConsumption();

  const cylinderOilSloc = calculateCylinderOilSloc(cylinderOilKgConsumption);

  const fuelSpecEntries = state.fuelSpecs()?.['entries'];
  const fuelTypeOptionsByRow =
    !fuelSpecEntries || isStateField(fuelSpecEntries)
      ? []
      : buildFuelTypeOptionsByRow(fuelSpecEntries, availableFuelTypeOptions);

  const requiredTotalConsumptionRowKeys = new Set<string>(['mainEngine']);
  const totalConsumptionRequiredRowsFulfilled = (): Set<string> => {
    const entries = state.totalConsumptions()?.['entries'];
    if (!entries || isStateField(entries)) return new Set();

    const fulfilledKeys = new Set<string>();
    requiredTotalConsumptionRowKeys.forEach((key) => {
      const totalForRow = (entries as unknown as Record<string, { value: unknown }>[]).reduce((acc, entry) => {
        const val = entry[key]?.value;
        return typeof val === 'number' && !isNaN(val) ? acc + val : acc;
      }, 0);

      if (totalForRow > 0) {
        fulfilledKeys.add(key);
      }
    });

    return fulfilledKeys;
  };

  const isTotalConsumptionRowRequired = (rowKey: string): boolean =>
    requiredTotalConsumptionRowKeys.has(rowKey) && !totalConsumptionRequiredRowsFulfilled().has(rowKey);

  return {
    cylinderOilKgConsumption,
    cylinderOilSloc,
    calculateAEBasicLoad,
    calculateTotal,
    lcvFieldsStatus,
    sulphurFieldsStatus,
    densityFieldsStatus,
    fuelTempFieldsStatus,
    fuelSpecsStatus,
    totalConsumptionsStatus,
    calculateABBasicLoad,
    fuelTypeOptionsByRow,
    isTotalConsumptionRowRequired,
  };
}

function calculateConsumptionsTotal(index: number, state: OffshoreConsumptionFormVmSections): number | undefined {
  const entries = state.totalConsumptions()['entries'];
  if (!entries || isStateField(entries)) return;
  const entry = entries[index];
  if (!entry) return;

  return Object.entries(entry)
    .filter(([k]) => k !== 'cargoRelated' && k !== 'total')
    .map(([, v]) => v.value)
    .filter((v): v is number => v != null && !Number.isNaN(v))
    .reduce((a, b) => a + b, 0);
}

const getCylinderOilValue = (fieldId: string, state: OffshoreConsumptionFormVmSections) => {
  const cylinderOilSection = state.cylinderOilConsumption() ?? {};
  const field = getFieldFromState(
    { cylinderOilConsumption: cylinderOilSection },
    'cylinderOilConsumption',
    fieldId
  ) as StateField<StateFieldType>;

  if (field == null || isNaN(field.value as number)) return 0;
  return field.value as number;
};

function areFieldsValid(fuelSpecs: FormSectionState, sectionName: string): boolean {
  const entries = fuelSpecs?.['entries'];
  if (!fuelSpecs || !entries || isStateField(entries)) return false;

  const entriesWithFuelType = entries.filter((entry) => entry['fuelType'] && entry['fuelType'].value);
  if (entriesWithFuelType.length === 0) return true;

  const hasValidField = (entry: Record<string, StateField<StateFieldType>>): boolean => {
    const field = entry[sectionName];
    if (!field) {
      return false;
    }
    return field.status === 'VALID' || field.pristine;
  };
  return entriesWithFuelType.every(hasValidField);
}
